import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Download, Phone, Printer } from 'lucide-react'
import { Logo } from '@/components/brand/logo'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { env } from '@/lib/env'
import { formatBytes } from '@/lib/files'
import { formatDay } from '@/lib/format'
import { reportLine, type ReportResults } from '@/features/queue/relux-report'

type Shared = {
  project: {
    name: string
    ref: string
    application: string | null
    building_type: string | null
    site_suburb: string | null
  }
  relux: { revision: string; completed_at: string | null }
  rep: { name: string; phone: string | null; email: string } | null
  spaces: { id: string; name: string }[]
  design_options: {
    id: string
    label: string
    is_primary: boolean
    notes: string | null
    items: {
      id: string
      qty: number | null
      space_id: string | null
      achieved_lux: number | null
      free_text: string | null
      notes: string | null
      product: {
        name: string
        sku: string
        lumens: number | null
        cct: string | null
        ip: string | null
        warranty_years: number | null
      } | null
    }[]
  }[]
  files: {
    id: string
    name: string
    category: string
    size: number | null
    url: string | null
    report?: ReportResults | null
  }[]
  expires_at: string
}

const shareUrl = (token: string, pdf = false) =>
  `${env.supabaseUrl}/functions/v1/share?token=${encodeURIComponent(token)}${pdf ? '&format=pdf' : ''}`

/** Fetches the branded PDF from the share function and saves it. */
async function downloadPack(token: string) {
  const res = await fetch(shareUrl(token, true), { headers: { apikey: env.supabaseKey ?? '' } })
  if (!res.ok) return
  const name =
    /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ??
    'Mesmerize Lighting design.pdf'
  const a = document.createElement('a')
  a.href = URL.createObjectURL(await res.blob())
  a.download = name
  a.click()
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000)
}

/** Public, branded, read-only design page for clients. No login; the link is the key. */
export const Route = createFileRoute('/share/$token')({ component: SharePage })

function SharePage() {
  const { token } = Route.useParams()
  const { data, error, isLoading } = useQuery({
    queryKey: ['shared', token],
    queryFn: async () => {
      const res = await fetch(shareUrl(token), { headers: { apikey: env.supabaseKey ?? '' } })
      if (!res.ok)
        throw new Error(
          'This link has expired or is not valid. Ask your Mesmerize contact for a new one.',
        )
      return (await res.json()) as Shared
    },
    retry: false,
  })

  if (isLoading) return <p className="p-8 text-center text-fg-muted">Loading the design...</p>
  if (error || !data)
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-6 p-8 text-center">
        <Logo on="theme" className="h-32" />
        <p className="text-lg">{error?.message}</p>
      </div>
    )

  const spaceName = (id: string | null) => data.spaces.find((s) => s.id === id)?.name
  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 py-8">
      <header className="relative overflow-hidden rounded-card bg-ink px-6 py-10 text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 right-0 h-64 w-96 rounded-full bg-brand/30 blur-3xl"
        />
        <Logo className="relative h-28" />
        <h1 className="relative mt-6 text-4xl uppercase">{data.project.name}</h1>
        <p className="relative mt-1 text-white/80">
          Lighting design - {data.project.ref} - Rev {data.relux.revision}
          {data.relux.completed_at && ` - ${formatDay(data.relux.completed_at)}`}
        </p>
        <p className="relative text-white/80">
          {[data.project.application, data.project.building_type, data.project.site_suburb]
            .filter(Boolean)
            .join(' - ')}
        </p>
      </header>

      {data.design_options.length > 0 && (
        <section className="flex flex-col gap-4">
          <h2 className="text-2xl uppercase">
            {data.design_options.length > 1
              ? `${data.design_options.length} options`
              : 'Fixture schedule'}
          </h2>
          <div className={data.design_options.length > 1 ? 'grid gap-4 lg:grid-cols-2' : ''}>
            {data.design_options.map((o) => (
              <div
                key={o.id}
                className="flex flex-col gap-3 rounded-card border-2 border-border bg-surface p-4"
              >
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-semibold uppercase">{o.label}</h3>
                  {o.is_primary && data.design_options.length > 1 && (
                    <Badge tone="brand">Recommended</Badge>
                  )}
                </div>
                {o.notes && <p className="text-fg-muted">{o.notes}</p>}
                <table className="w-full text-left text-sm">
                  <thead className="text-fg-muted">
                    <tr>
                      <th className="py-1 pr-2">Qty</th>
                      <th className="py-1 pr-2">Product</th>
                      <th className="py-1">Where</th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.items.map((i) => (
                      <tr key={i.id} className="border-t border-border align-top">
                        <td className="py-2 pr-2 font-semibold">{i.qty}</td>
                        <td className="py-2 pr-2">
                          <span className="font-semibold">{i.product?.name ?? i.free_text}</span>
                          {i.product && (
                            <span className="block text-fg-muted">
                              {[
                                i.product.sku,
                                i.product.lumens && `${i.product.lumens} lm`,
                                i.product.cct,
                                i.product.ip,
                                i.product.warranty_years &&
                                  `${i.product.warranty_years}-year warranty`,
                              ]
                                .filter(Boolean)
                                .join(' - ')}
                            </span>
                          )}
                        </td>
                        <td className="py-2">
                          {spaceName(i.space_id)}
                          {i.achieved_lux && (
                            <span className="block text-fg-muted">{i.achieved_lux} lux</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.files.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-2xl uppercase">Design files</h2>
          <ul className="flex flex-col gap-2">
            {data.files.map((f) => (
              <li
                key={f.id}
                className="flex flex-wrap items-center gap-3 rounded-control border border-border bg-surface p-3"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">{f.name}</span>
                  <span className="text-sm text-fg-muted">
                    {f.category}
                    {f.size ? ` - ${formatBytes(f.size)}` : ''}
                  </span>
                  {reportLine(f.report) && (
                    <span className="block text-sm font-semibold text-brand-text">
                      {reportLine(f.report)}
                    </span>
                  )}
                </span>
                {f.url && (
                  <Button asChild size="sm">
                    <a href={f.url} target="_blank" rel="noreferrer">
                      <Download aria-hidden /> Download
                    </a>
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <footer className="flex flex-wrap items-center gap-4 border-t border-border pt-6 print:hidden">
        {data.rep && (
          <p className="flex-1">
            Questions? Contact <strong>{data.rep.name}</strong>
            {data.rep.phone && (
              <a
                className="ml-2 inline-flex items-center gap-1 font-semibold underline"
                href={`tel:${data.rep.phone}`}
              >
                <Phone className="size-4" aria-hidden /> {data.rep.phone}
              </a>
            )}
          </p>
        )}
        <Button onClick={() => void downloadPack(token)}>
          <Download aria-hidden /> Download client pack
        </Button>
        <Button variant="outline" onClick={() => window.print()}>
          <Printer aria-hidden /> Print this page
        </Button>
        <p className="w-full text-sm text-fg-muted">
          Mesmerize Lighting - mezlighting.co.za. This link expires on {formatDay(data.expires_at)}.
        </p>
      </footer>
    </div>
  )
}
